// How the leave calendar reads a refused leave write (P18, P40).
//
// The three leave endpoints (updateleavedate, updateleavedate-range,
// cancel-and-book-leave-range — Backend/api/controllers/doctorController.js)
// refuse in four ways the doctor must be able to tell apart:
//
//   409 LEAVE_HAS_BOOKINGS        { affectedCount, conflictDates, message{en,fr} }
//       Live visits (Booked + Rescheduling) sit inside the leave. Not an error
//       to show — a QUESTION to ask: cancel them and notify, or keep the leave
//       off. Answering "cancel and notify" resends with cancelAndNotify: true.
//   400 duplicate                 { duplicateDates, message }
//       A leave already exists on one of the dates (one leave per date).
//   400 LEAVE_BEYOND_FUTURE_LIMIT { limitMonths, latestDate, beyondDates, message{en,fr} }
//       Further ahead than this hospital lets a leave be applied.
//   anything else                 whatever sentence the server sent.
//
// The thunks reject with `{ error: axiosError }`; a bare axios error or the
// response body itself are accepted too, so a later change to the slice's
// rejection shape does not silently turn every refusal into "failed".
import { extractErrorMessage } from '../../utils/extractErrorMessage';

/** The response body and status out of whatever the thunk rejected with. */
const responseOf = (rejected) => {
    const err = rejected?.error ?? rejected;
    if (err?.response) return { status: err.response.status ?? null, data: err.response.data || {} };
    if (err && typeof err === 'object' && (err.code || err.duplicateDates)) return { status: err.status ?? null, data: err };
    return { status: null, data: {} };
};

/**
 * @returns {{ kind: 'hasBookings'|'duplicate'|'beyondLimit'|'other', status: number|null, data: object }}
 */
export const readLeaveRefusal = (rejected) => {
    const { status, data } = responseOf(rejected);
    if (data.code === 'LEAVE_HAS_BOOKINGS') return { kind: 'hasBookings', status, data };
    if (data.code === 'LEAVE_BEYOND_FUTURE_LIMIT') return { kind: 'beyondLimit', status, data };
    if (Array.isArray(data.duplicateDates) && data.duplicateDates.length) return { kind: 'duplicate', status, data };
    return { kind: 'other', status, data };
};

/** "2026-09-16" as the reader's own calendar writes it; anything else as given. */
export const formatLeaveDay = (day, lang = 'en') => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
    if (!m) return String(day || '');
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    try {
        return d.toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
        return String(day);
    }
};

export const formatLeaveDays = (days, lang) => (Array.isArray(days) ? days : [])
    .map((d) => formatLeaveDay(d, lang))
    .join('; ');

/**
 * The question asked when a leave covers booked visits.
 * @returns {{ title: string, text: string }}
 */
export const leaveBookingsQuestion = (refusal, t, lang) => {
    const n = Number(refusal?.data?.affectedCount) || 0;
    const dates = formatLeaveDays(refusal?.data?.conflictDates, lang);
    return {
        title: t('leavedatescal.leave_bookings_title', 'Patients are booked on these dates'),
        text: n === 1
            ? t('leavedatescal.leave_bookings_one_text', {
                dates,
                defaultValue: '1 patient is booked on these dates ({{dates}}) — cancel the visit and notify the patient, or keep the leave off.',
            })
            : t('leavedatescal.leave_bookings_many_text', {
                n,
                dates,
                defaultValue: '{{n}} patients are booked on these dates ({{dates}}) — cancel them and notify, or keep the leave off.',
            }),
    };
};

/** A refusal (other than the bookings question) as one sentence to show. */
export const leaveRefusalSentence = (rejected, t, lang, fallback) => {
    const refusal = rejected?.kind ? rejected : readLeaveRefusal(rejected);
    const { kind, data } = refusal;
    if (kind === 'duplicate') {
        return t('leavedatescal.leave_duplicate_dates', {
            dates: formatLeaveDays(data.duplicateDates, lang),
            defaultValue: 'A leave already exists on {{dates}}. Edit that leave instead of adding a second one.',
        });
    }
    if (kind === 'beyondLimit') {
        return t('leavedatescal.leave_beyond_limit', {
            months: data.limitMonths,
            latest: formatLeaveDay(data.latestDate, lang),
            dates: formatLeaveDays(data.beyondDates, lang),
            defaultValue: 'This hospital allows leave at most {{months}} months ahead (up to {{latest}}). Not allowed: {{dates}}.',
        });
    }
    if (kind === 'hasBookings') {
        return leaveBookingsQuestion(refusal, t, lang).text;
    }
    return extractErrorMessage(rejected?.kind ? data : rejected, fallback, lang);
};
